import React, { useMemo } from 'react';
import { Typography } from 'antd';
import type { ClosingPackageProjection } from '../types';
import {
  buildClosingPackageSummaryMetrics,
  formatClosingPackageSummaryDate,
  formatClosingPackageSummaryDateTime,
  formatReadinessValue,
  formatRequiredValue,
} from '../lib/summaryMetrics';
import './closingPackageWorkspace.css';

const { Text } = Typography;

export type ClosingPackageSummaryCardsProps = {
  projection: ClosingPackageProjection;
};

type MetricTone = 'default' | 'accent' | 'warn' | 'ok';

type MetricCardModel = {
  key: string;
  label: string;
  value: string;
  hint: string;
  tone: MetricTone;
};

/**
 * A1b Summary KPI — Design Freeze Variant B / FE-003.
 * Compact equal-height metric cards. Projection display only.
 */
const ClosingPackageSummaryCards: React.FC<ClosingPackageSummaryCardsProps> = ({ projection }) => {
  const metrics = useMemo(() => buildClosingPackageSummaryMetrics(projection), [projection]);

  const cards: MetricCardModel[] = useMemo(() => {
    const missingTone: MetricTone = metrics.missingRequired > 0 ? 'warn' : 'ok';
    const blockersTone: MetricTone = metrics.blockersCount > 0 ? 'warn' : 'default';

    return [
      {
        key: 'readiness',
        label: 'Готовность',
        value: formatReadinessValue(metrics),
        hint: 'позиции комплекта',
        tone: 'accent',
      },
      {
        key: 'required',
        label: 'Обязательные',
        value: formatRequiredValue(metrics),
        hint: 'готовы / всего',
        tone: 'default',
      },
      {
        key: 'missing',
        label: 'Отсутствует',
        value: String(metrics.missingRequired),
        hint: 'обязательных',
        tone: missingTone,
      },
      {
        key: 'blockers',
        label: 'Blockers',
        value: String(metrics.blockersCount),
        hint: 'блокирующих',
        tone: blockersTone,
      },
      {
        key: 'lastDispatch',
        label: 'Последняя отправка',
        value: formatClosingPackageSummaryDate(metrics.lastDispatchAt),
        hint: metrics.lastDispatchAt ? 'сканы' : 'нет отправок',
        tone: 'default',
      },
      {
        key: 'lastChanged',
        label: 'Последнее изменение',
        value: formatClosingPackageSummaryDateTime(metrics.lastChangedAt),
        hint: 'комплект',
        tone: 'default',
      },
    ];
  }, [metrics]);

  return (
    <div className="cp-summary cp-span-full" data-testid="closing-package-summary-kpi">
      <div className="cp-summary__grid">
        {cards.map((card) => (
          <div
            key={card.key}
            className={`cp-summary__card cp-summary__card--${card.tone}`}
            data-metric={card.key}
          >
            <Text type="secondary" className="cp-summary__label">
              {card.label}
            </Text>
            <div className="cp-summary__value">{card.value}</div>
            <Text type="secondary" className="cp-summary__hint">
              {card.hint}
            </Text>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClosingPackageSummaryCards;
